import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import { StorageService } from '@core/services';
import { SvgIconComponent } from 'angular-svg-icon';
import { ActivatedRoute, RouterLink } from '@angular/router';
import posthog, { CaptureResult } from 'posthog-js';
import { Account } from '@legacy-models/portal/Account';
import { FeatureCard } from '@legacy-models/portal/FeatureCard';

interface CardPosthogEventProperties {
  module: string;
  vertical?: string;
  segment?: string;
}

@Component({
  selector: 'oke-card',
  templateUrl: './card.html',
  styleUrl: './card.scss',
  imports: [RouterLink, SvgIconComponent]
})
export class Card implements OnInit, AfterViewInit, OnDestroy {
  readonly $feature: InputSignal<FeatureCard> = input.required<FeatureCard>({
    alias: 'feature'
  });
  acct: Account | null = null;
  private readonly _hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _storageSvc = inject(StorageService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _$posthogEvtProperties: WritableSignal<CardPosthogEventProperties> =
    signal<CardPosthogEventProperties>({
      module: ''
    });

  ngOnDestroy(): void {
    this._hostElement.nativeElement.removeEventListener('mouseenter', this._doFocusCard);
    this._hostElement.nativeElement.removeEventListener('mouseleave', this._undoFocusCard);
  }

  ngOnInit(): void {
    this._storageSvc.currentAccount$.subscribe(currAcc => {
      this.acct = currAcc;
    });
    this._$posthogEvtProperties.set({
      module: this.$feature().Name
    });
    this._route.data.subscribe(dataObject => {
      if (Object.prototype.hasOwnProperty.call(dataObject, 'vertical')) {
        this._$posthogEvtProperties.update(posthogEvtProperties => {
          return {
            ...posthogEvtProperties,
            vertical: dataObject['vertical']
          };
        });
      }
      if (Object.prototype.hasOwnProperty.call(dataObject, 'segment')) {
        this._$posthogEvtProperties.update(posthogEvtProperties => {
          return {
            ...posthogEvtProperties,
            segment: dataObject['segment']
          };
        });
      }
    });
  }

  ngAfterViewInit(): void {
    this._hostElement.nativeElement.addEventListener('mouseenter', this._doFocusCard);
    this._hostElement.nativeElement.addEventListener('mouseleave', this._undoFocusCard);
  }

  protected trackCardClick = (): CaptureResult | undefined =>
    posthog.capture('dashboard:card_click', this._$posthogEvtProperties());

  private _doFocusCard(evt: MouseEvent): void {
    const focusedCard = evt.currentTarget as HTMLElement;
    const parentElement = focusedCard.parentElement;
    if (!parentElement) {
      return;
    }

    const cardSiblings = Array.from(parentElement.querySelectorAll('oneok-card')).filter(
      oneokCard => oneokCard !== focusedCard
    );
    cardSiblings.forEach(blurredCard => (blurredCard as HTMLElement).classList.add('blurred'));
  }

  private _undoFocusCard(evt: MouseEvent): void {
    const parentElement = (evt.currentTarget as HTMLElement).parentElement;
    if (!parentElement) {
      return;
    }

    Array.from(parentElement.querySelectorAll('oneok-card')).forEach(card =>
      (card as HTMLElement).classList.remove('blurred')
    );
  }
}
